import JSPDF from 'jspdf';
import AutoTable from 'jspdf-autotable';

import {
	calculateYCoordinate,
	checkNewPageNeeded,
	toSentenceCase,
	wrapText,
} from 'common/utils/utilFunctions';

import { IAIModelPDFDownload } from 'features/AiSimulation/api/type-def';

const getRows = (data: any) => {
	let rows = [];

	try {
		rows = typeof data === 'string' ? JSON.parse(data) : data;
	} catch (error) {
		rows = [];
	}

	rows = Array.isArray(rows) ? rows : [];
	rows = rows.map((row) => [row.display_id, row.name, row.score]);
	return rows;
};

const setPDFStyles = (doc: JSPDF, props: IAIModelPDFDownload) => {
	const {
		title,
		inputTitle,
		inputValue,
		outputTitle,
		outputValue,
		isControlStatement,
		controlStatementInputItem,
		isControlGrammarCheck,
		controlGrammarCheckOutputItem,
	} = props;

	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.height;
	const margin = 10; // Add some margin
	const textWidth = pageWidth - 2 * margin;
	let currentY = margin;
	const lineHeight = 6;

	const addNormalText = (
		fontType: 'normal' | 'bold',
		fontSize: number,
		text?: string,
	) => {
		doc.setFontSize(fontSize);
		doc.setFont('helvetica', fontType);
		currentY = checkNewPageNeeded(
			wrapText(doc, textWidth, text).length,
			currentY,
			lineHeight,
			pageHeight,
			margin,
			doc,
		);
		doc.text(wrapText(doc, textWidth, text), margin, currentY);
		currentY = calculateYCoordinate(
			wrapText(doc, textWidth, text).length,
			lineHeight,
			currentY,
		);
	};

	addNormalText('bold', 24, toSentenceCase(title));

	addNormalText('bold', 20, toSentenceCase(inputTitle));

	addNormalText('normal', 14, inputValue);

	if (isControlStatement) {
		addNormalText('normal', 14, toSentenceCase('Control Type'));
		addNormalText('normal', 14, controlStatementInputItem?.controlType);

		addNormalText('normal', 14, toSentenceCase('Control Owner'));
		addNormalText('normal', 14, controlStatementInputItem?.controlOwner);

		addNormalText('normal', 14, toSentenceCase('Control Frequency'));
		addNormalText('normal', 14, controlStatementInputItem?.controlFrequency);
	}

	if (!isControlGrammarCheck) {
		addNormalText('bold', 20, toSentenceCase(outputTitle));

		doc.setFontSize(14);
		doc.setFont('helvetica', 'normal');
		currentY = checkNewPageNeeded(
			wrapText(doc, textWidth, outputValue).length,
			currentY,
			lineHeight,
			pageHeight,
			margin,
			doc,
		);
		doc.text(wrapText(doc, textWidth, outputValue), 20, currentY);
	} else {
		addNormalText('bold', 20, toSentenceCase('Five W Response'));
		addNormalText('normal', 14, controlGrammarCheckOutputItem?.fiveWResponse);

		addNormalText('bold', 20, toSentenceCase('Grammar Score'));
		addNormalText('normal', 14, controlGrammarCheckOutputItem?.grammarScore);

		if (controlGrammarCheckOutputItem?.additionalInfo) {
			addNormalText('bold', 20, toSentenceCase('Additional Information'));
			addNormalText(
				'normal',
				14,
				controlGrammarCheckOutputItem?.additionalInfo,
			);
		}

		addNormalText('bold', 20, toSentenceCase(outputTitle));
		addNormalText('normal', 14, outputValue);

		if (JSON.parse(controlGrammarCheckOutputItem?.controlMapping).length > 0) {
			addNormalText('bold', 20, toSentenceCase('Similar Controls'));

			const tableData = controlGrammarCheckOutputItem?.controlMapping;

			const columns = [
				{ header: 'Control id', dataKey: 'controlId' },
				{ header: 'Control name', dataKey: 'controlName' },
				{ header: 'Similarity score', dataKey: 'similarityScore' },
			];

			AutoTable(doc, {
				head: [columns.map((col) => col.header)], // Extract headers
				body: getRows(tableData), // Extract row values
				startY: currentY, // Start position of the table
				theme: 'grid', // Table style: 'striped', 'grid', or 'plain'
				styles: {
					lineColor: [191, 191, 191],
					lineWidth: 1, // Black border color
					halign: 'center', // Default horizontal alignment
					fontSize: 14, // Set font size
				},
				headStyles: {
					textColor: [0, 0, 0], // Black header text color
					fontStyle: 'bold',
					fillColor: [240, 240, 240],
					halign: 'center', // Header text centered
				},
				bodyStyles: {
					halign: 'left', // Align body text to the left
					lineWidth: 1,
					lineColor: [191, 191, 191],
					fontSize: 10,
					cellPadding: 5,
				},
			});
		}
	}
};

export const aiModelPDFDownload = async (
	recordName: string | null,
	stepName: string,
	title: string,
	inputItem: {
		inputTitle: string;
		inputValue: string;
	},
	outputItem: {
		outputTitle: string;
		outputValue: string;
	},
	controlStatementInfo?: {
		controlType: string;
		controlOwner: string;
		controlFrequency: string;
	},
	controlGrammarCheckInfo?: {
		fiveWResponse: string;
		grammarScore: string;
		controlMapping: any;
		additionalInfo: string;
	},
) => {
	const tempfileName = recordName ?? stepName;
	const { inputTitle, inputValue } = inputItem;
	const { outputTitle, outputValue } = outputItem;

	const doc = new JSPDF();

	const fileName = `${tempfileName}.pdf`;

	setPDFStyles(doc, {
		fileName,
		title,
		inputTitle,
		outputTitle,
		inputValue,
		outputValue,
		isControlStatement: !!controlStatementInfo,
		controlStatementInputItem: controlStatementInfo,
		isControlGrammarCheck: !!controlGrammarCheckInfo,
		controlGrammarCheckOutputItem: controlGrammarCheckInfo,
	});
	doc.save(fileName);
};
